import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import PopupContent from "../components/popupContent";
import {
  apiCreateOrUpdatePigment,
  apiGetAllPigments,
  apiGetPigmentDetail,
} from "../services/PigmentsService";
import {
  apiGetSampleCardDetail,
  apiSampleCardCreateOrUpdate,
} from "../services/SampleCardsService";

import PopupOperationsBar from "./PopupOperationsBar";
import iconVzorkovnice from "../images/buttons/vzorky.png";
import { addNotification } from "../redux/actions";
import { model } from "../validation/models/sampleCard";
import { validateForm } from "../validation/formValidation";
import { ValidationProvider } from "../providers/ValidationContext";
import { useTranslation } from "react-i18next";
import TranslationContext from "../providers/TranslationContext";
import { FormatString } from "../helpers/stringHelpers";
import CheckBox from "../components/common/CheckBox";
import Button from "../components/common/Button";
import ComboBoxData from "../components/common/ComboBoxData";
// styles
require("./PopupGeneralStyles.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
  sampleCards: any;
}

const SampleCardPopup = (props: Props) => {
  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));
  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);

  const [saveError, setSaveError] = useState(false);
  const dispatch = useDispatch();

  const isEditMode = props.id ? true : false;

  const sampleCardsForSorting = props.id ? props.sampleCards.filter(a => a.id != props.id) : null;
  const defaultSampleCardForSorting = sampleCardsForSorting ? sampleCardsForSorting[0].id : 0;
  const [sortOrderOptions, setSortOrderOptions] = useState({ 'changeSortOrder': false, 'direction': 'before', 'sampleCardId': defaultSampleCardForSorting })

  useEffect(() => {
    if (props.id) {
      apiGetSampleCardDetail(props.id).then((responseData) => {
        setFormData(responseData.data);

        setLoading(false);
      }).catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
        props.handleCancel();
      });
    } else {
      setLoading(false);
    }
  }, []);

  const onAddingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiSampleCardCreateOrUpdate(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.name), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiSampleCardCreateOrUpdate(
        {
          ...formData,
          UpdateSortOrder: sortOrderOptions.changeSortOrder,
          SortOrderDirectionIsBefore: sortOrderOptions.direction == "before",
          SortOrderSampleCardId: sortOrderOptions.sampleCardId
        })
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.name), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };


  const handleSampleCardChange = (value: number) => {
    setSortOrderOptions({ ...sortOrderOptions, sampleCardId: value });
  };

  return (
    <PopupContent
      headerIcon={iconVzorkovnice}
      header={props.id ? t('EditLabel') : t('CreateLabel')}
    >
      <ValidationProvider value={validationModel}>
        <TextBox
          dataField="name"
          defaultValue={formData.name}
          formData={formData}
          setStateMethod={setFormData}
          id="name"
          label={t('Labels.name')}
          placeholder={t('Labels.namePlaceholder')}
        />

        <TextArea
          id="noteInternal"
          label={t('Labels.note')}
          placeholder={t('Labels.notePlaceholder')}
          rows={4}
          dataField="noteInternal"
          formData={formData}
          setStateMethod={setFormData}
          value={formData.noteInternal}

        />

        {props.id && <>
          <CheckBox
            id="changeSortOrder"
            label={`${t('Labels.changeSortOrder')}`}
            paddingLeft={"10px"}
            handleCheckedChange={(checked) => {

              setSortOrderOptions({ ...sortOrderOptions, changeSortOrder: checked });
            }}
            defaultValue={false}
          />

          {sortOrderOptions.changeSortOrder &&
            <div className="sortOrderOptions">
              <Button iconClass="" bold={sortOrderOptions.direction == 'before'} onClick={() => { setSortOrderOptions({ ...sortOrderOptions, direction: 'before' }) }} text={t('BeforeSampleCard')} />
              <Button iconClass="" bold={sortOrderOptions.direction == 'after'} onClick={() => { setSortOrderOptions({ ...sortOrderOptions, direction: 'after' }) }} text={t('AfterSampleCard')} />

              <ComboBoxData
                id="sampleCards"
                label={t('SampleCard')}
                dataTextField="name"
                dataValueField="id"
                defaultValue={defaultSampleCardForSorting}
                handleChange={handleSampleCardChange}
                rows={sampleCardsForSorting}
              />
            </div>
          }

        </>}

        <PopupOperationsBar
          handleAdding={onAddingClick}
          handleCancellation={onCancelClick}
          handleEditing={onEditingClick}
          id={props.id}
        />
      </ValidationProvider>
    </PopupContent>
  );
};

export default SampleCardPopup;

